import { useEffect, useRef, useState } from 'react'
import {
  CUSTOM,
  PRESETS,
  TODAY,
  describe,
} from './range.js'

/**
 * The dashboard's date range control.
 *
 * A menu rather than two date boxes, because the answer a shopkeeper wants is
 * almost always one of six words and almost never a pair of dates. Custom is
 * there for the seventh case and stays folded away until it is asked for.
 *
 * `anchor` is the day the server named. Until it arrives every preset but Today
 * is disabled: they are all computed from it, and offering a control that
 * silently does nothing is worse than one that is visibly not ready yet.
 */
export default function RangePicker({
  preset,
  dates,
  custom,
  anchor,
  formatDate,
  onPreset,
  onCustom,
}) {
  const [open, setOpen] = useState(false)
  const box = useRef(null)
  const button = useRef(null)

  const current = PRESETS.find((p) => p.id === preset) ?? PRESETS[0]
  const caption = describe(preset, dates, formatDate)
  const ready = Boolean(anchor)

  // Close on a click outside or on Escape. Both, because a menu that only
  // closes on Escape traps a mouse user and one that only closes on click
  // traps a keyboard user.
  useEffect(() => {
    if (!open) return undefined
    function onDown(event) {
      if (!box.current?.contains(event.target)) setOpen(false)
    }
    function onKey(event) {
      if (event.key === 'Escape') {
        setOpen(false)
        button.current?.focus()
      }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  function choose(id) {
    onPreset(id)
    // Custom keeps the panel open: choosing it is the start of the interaction,
    // not the end of one.
    if (id !== CUSTOM) setOpen(false)
  }

  return (
    <div className="range" ref={box}>
      <button
        ref={button}
        type="button"
        className="range-button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        <span className="range-label">{current.label}</span>
        {caption ? <span className="range-dates">{caption}</span> : null}
        <span className="range-caret" aria-hidden="true">▾</span>
      </button>

      {open ? (
        <div className="range-menu" role="menu" aria-label="Date range">
          {PRESETS.map((option) => {
            // Today needs no anchor — it sends no dates at all.
            const disabled = !ready && option.id !== TODAY
            return (
              <button
                key={option.id}
                type="button"
                role="menuitemradio"
                aria-checked={option.id === preset}
                className="range-option"
                disabled={disabled}
                onClick={() => choose(option.id)}
              >
                <span className="range-tick" aria-hidden="true">
                  {option.id === preset ? '✓' : ''}
                </span>
                {option.label}
              </button>
            )
          })}

          {preset === CUSTOM ? (
            <div className="range-custom">
              <label>
                <span>From</span>
                <input
                  type="date"
                  className="ui-input"
                  value={custom?.date_from ?? ''}
                  max={anchor ?? undefined}
                  onChange={(e) => onCustom({ ...custom, date_from: e.target.value })}
                />
              </label>
              <label>
                <span>To</span>
                <input
                  type="date"
                  className="ui-input"
                  value={custom?.date_to ?? ''}
                  max={anchor ?? undefined}
                  onChange={(e) => onCustom({ ...custom, date_to: e.target.value })}
                />
              </label>
              {/* Both dates are capped at the shop's own today: a range ending
                  next week is not a question the day book can answer, and an
                  empty result would read as a shop that sold nothing. */}
              <p className="range-hint">
                {custom?.date_from && custom?.date_to
                  ? 'Showing this range.'
                  : 'Pick both dates to apply.'}
              </p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
